import React, {ReactNode, useContext, useEffect} from 'react';
import {doesSessionExist} from 'supertokens-auth-react/recipe/session';

import ContextViewFileList, {I_dir_id_history} from './ContextViewFileList';
import {AccessibleDocument, DirView, DocumentsService, ApiError} from '../../services/openapi';
import APIWrapperContext from '../../services/APIWrapperContext';

interface IContextProviderViewFileListProps {
  children?: ReactNode;
  user_specific?: boolean;
  hausverwaltung?: boolean;
}

const ContextProviderViewFileList = ({
  children,
  user_specific,
  hausverwaltung,
}: IContextProviderViewFileListProps) => {
  const {call_backend_api} = useContext(APIWrapperContext);

  const [dir_id_history, setDirIdHistory] = React.useState<I_dir_id_history[]>([]);
  const [curr_dir_id, setCurrDirId] = React.useState<string | undefined>();
  const [curr_dir_view, setCurrDirView] = React.useState<DirView | {}>({});
  const [loading_dir, setLoadingDir] = React.useState<boolean>(false);
  const [accessible_elements, setAccessibleElements] = React.useState<
    Record<string, AccessibleDocument>
  >({});
  const [error, setError] = React.useState<string | null>(null);
  const [isInitialized, setIsInitialized] = React.useState(false);

  const update_accessible_elements = () => {
    if (user_specific) {
      return;
    }

    setError(null);
    call_backend_api(
      DocumentsService.getAccessibleDocsDocGetAccessibleDocsGet(),
      (response: Record<string, AccessibleDocument>) => {
        setAccessibleElements(response);
      },
      (error: ApiError) => {
        setError('Failed to load accessible documents');
        console.error('Error loading accessible documents:', error);
      },
      undefined,
      false,
      undefined,
      undefined,
      true
    );
  };

  const dir_navigate_back = (id?: string) => {
    if (dir_id_history.length > 1) {
      let tmp_dir_id_history = dir_id_history;
      if (id) {
        setCurrDirId(id);
        setDirIdHistory(
          tmp_dir_id_history.slice(0, tmp_dir_id_history.findIndex((el) => el.id === id) + 1)
        );
      } else {
        setCurrDirId(tmp_dir_id_history[tmp_dir_id_history.length - 2].id);
        setDirIdHistory(tmp_dir_id_history.slice(0, tmp_dir_id_history.length - 1));
      }
    }
  };

  const dir_navigate_forward = (id: string) => {
    if ('children' in curr_dir_view) {
      let name = curr_dir_view.children?.find((el) => el.hidrive_id === id)?.parent;
      setDirIdHistory([...dir_id_history, {id: id, name: name ? name : ''}]);
    }
    setCurrDirId(id);
  };

  const update_dir_view = (id: string | undefined) => {
    if (loading_dir || error) {
      return;
    }

    if (!doesSessionExist()) {
      setError('Sie sind nicht eingeloggt.');
      setLoadingDir(false);
      return;
    }

    setError(null);
    setLoadingDir(true);

    const processDirView = (dirView: DirView): DirView => {
      return {
        ...dirView,
        children: dirView.children?.map((child) => ({
          ...child,
          isUnread: child.isUnread || false,
          children: child.children ? processDirView(child).children : [],
        })),
      };
    };

    const handleSuccess = (response: DirView) => {
      const processedResponse = processDirView(response);
      setCurrDirView(processedResponse);
      if (id === undefined) {
        setDirIdHistory([...dir_id_history, {id: response.hidrive_id, name: response.parent}]);
      }
      setLoadingDir(false);
    };

    const handleError = (error: ApiError) => {
      setError('Failed to load directory content');
      console.error('Error loading directory:', error);
      setLoadingDir(false);
    };

    if (user_specific) {
      console.log('update_dir_view', id);
      call_backend_api(
        DocumentsService.hidriveGetFilesUserDocGetDirContentUserGet(id),
        handleSuccess,
        handleError,
        undefined,
        false,
        undefined,
        'Laden des Ordners fehlgeschlagen',
        false
      );
    } else {
      call_backend_api(
        DocumentsService.hidriveGetFilesDocGetDirContentGet(
          id,
          hausverwaltung ? hausverwaltung : false
        ),
        handleSuccess,
        handleError,
        undefined,
        false,
        undefined,
        'Laden des Ordners fehlgeschlagen',
        false
      );
    }
  };

  const init_dir_view = () => {
    if (isInitialized) {
      return;
    }
    setIsInitialized(true);
    update_accessible_elements();
    update_dir_view(undefined);
  };

  // Initialize the component on mount
  useEffect(() => {
    init_dir_view();
  }, []);

  // Update directory view when curr_dir_id changes
  useEffect(() => {
    if (curr_dir_id && !error) {
      update_dir_view(curr_dir_id);
    }
  }, [curr_dir_id]);

  return (
    <ContextViewFileList.Provider
      value={{
        dir_id_history,
        curr_dir_id,
        curr_dir_view,
        accessible_elements,
        user_specific,
        loading_dir,
        error,
        update_accessible_elements,
        setCurrDirId,
        setCurrDirView,
        init_dir_view,
        dir_navigate_back,
        dir_navigate_forward,
        update_dir_view,
      }}>
      {children}
    </ContextViewFileList.Provider>
  );
};

export default ContextProviderViewFileList;
